import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/use-auth";
import { apiRequest } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import Sidebar from "../components/layout/sidebar";
import MobileSidebar from "../components/layout/mobile-sidebar";
import TopNav from "../components/layout/top-nav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, UserPlus, Copy, Loader2, Send, UserMinus, Pencil } from "lucide-react";
import { validateEmail } from "../lib/validation";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "agent", label: "Agent" },
  { value: "worker", label: "Worker" },
  { value: "view_only", label: "View only" },
  { value: "accountant", label: "Accountant" },
];

type TeamUser = {
  id: number;
  contractorId: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role: string;
  isActive: boolean;
  invitedAt: string | null;
  acceptedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

type TeamPageProps = { embedInSettings?: boolean };

export default function TeamPage({ embedInSettings }: TeamPageProps = {}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("manager");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [removeMember, setRemoveMember] = useState<TeamUser | null>(null);
  const [editingMember, setEditingMember] = useState<TeamUser | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editRole, setEditRole] = useState("manager");

  // Only owner and admin can manage team; no fallback so team users (agent, manager, etc.) never see invite
  const userWithRole = user as { role?: string; userId?: number | null };
  const userRole = userWithRole?.role;
  const canManageTeam = userRole === "owner" || userRole === "admin" || userRole === "contractor";

  const { data: teamUsers = [], isLoading } = useQuery<TeamUser[]>({
    queryKey: ["/api/protected/team"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/team");
      return res.json();
    },
    enabled: canManageTeam,
  });

  const updateMemberMutation = useMutation({
    mutationFn: async (payload: { userId: number; email?: string; firstName?: string; lastName?: string; phone?: string; role?: string }) => {
      const res = await apiRequest("POST", "/api/protected/team-update", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/team"] });
      setEditingMember(null);
      toast({ title: "Saved", description: "Team member updated." });
    },
    onError: (err: Error) => {
      try {
        const data = JSON.parse(err.message.split(": ")[1] || "{}");
        toast({ title: data.error || "Error", description: data.message || err.message, variant: "destructive" });
      } catch {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", "/api/protected/team-resend-invite", { userId });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/team"] });
      if (data.inviteLink) {
        navigator.clipboard.writeText(data.inviteLink);
        toast({
          title: "Invite resent",
          description: "New invite link copied to clipboard. Share it with the team member.",
        });
      }
    },
    onError: (err: Error) => {
      try {
        const data = JSON.parse(err.message.split(": ")[1] || "{}");
        toast({
          title: data.error || "Error",
          description: data.message || err.message,
          variant: "destructive",
        });
      } catch {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", "/api/protected/team-remove", { userId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/team"] });
      setRemoveMember(null);
      toast({
        title: "Member removed",
        description: "They can no longer log in to this account.",
      });
    },
    onError: (err: Error) => {
      try {
        const data = JSON.parse(err.message.split(": ")[1] || "{}");
        toast({
          title: data.error || "Error",
          description: data.message || err.message,
          variant: "destructive",
        });
      } catch {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async (body: { email: string; firstName: string; lastName: string; phone?: string; role: string }) => {
      const res = await apiRequest("POST", "/api/protected/team", body);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/team"] });
      setInviteLink(data.inviteLink ?? null);
      setEmail("");
      setFirstName("");
      setLastName("");
      setPhone("");
      setRole("manager");
      toast({
        title: "Invite sent",
        description: data.message,
      });
    },
    onError: (err: Error) => {
      try {
        const data = JSON.parse(err.message.split(": ")[1] || "{}");
        toast({
          title: data.error || "Error",
          description: data.message || err.message,
          variant: "destructive",
        });
      } catch {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      }
    },
  });

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      toast({ title: "Copied", description: "Invite link copied to clipboard" });
    }
  };

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setInviteLink(null);
  };

  useEffect(() => {
    if (editingMember) {
      setEditEmail(editingMember.email);
      setEditFirstName(editingMember.firstName);
      setEditLastName(editingMember.lastName);
      setEditPhone(editingMember.phone ?? "");
      setEditRole(editingMember.role || "manager");
    }
  }, [editingMember]);

  const handleSaveEdit = () => {
    if (!editingMember) return;
    const emailCheck = validateEmail(editEmail);
    if (!emailCheck.valid || !emailCheck.normalized) {
      toast({
        title: "Invalid email",
        description: emailCheck.error || "Use a full email address (e.g. name@company.com)",
        variant: "destructive",
      });
      return;
    }
    if (!editFirstName.trim() || !editLastName.trim()) {
      toast({ title: "Missing fields", description: "First name and last name are required", variant: "destructive" });
      return;
    }
    updateMemberMutation.mutate({
      userId: editingMember.id,
      email: emailCheck.normalized,
      firstName: editFirstName.trim(),
      lastName: editLastName.trim(),
      phone: editPhone.trim() || undefined,
      role: editRole,
    });
  };

  const handleInvite = () => {
    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      toast({
        title: "Missing fields",
        description: "Email, first name, and last name are required",
        variant: "destructive",
      });
      return;
    }
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid || !emailCheck.normalized) {
      toast({
        title: "Invalid email",
        description: emailCheck.error || "Use a full email address (e.g. name@company.com)",
        variant: "destructive",
      });
      return;
    }
    inviteMutation.mutate({ email: emailCheck.normalized, firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() || undefined, role });
  };

  if (!user) return null;
  if (!canManageTeam) {
    const noAccess = (
      <Card className="max-w-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100">Team</CardTitle>
          <CardDescription className="text-slate-600 dark:text-slate-400">You don&apos;t have permission to manage team members. Only owners and admins can.</CardDescription>
        </CardHeader>
      </Card>
    );
    if (embedInSettings) return <div className="space-y-6">{noAccess}</div>;
    return (
      <div className="remodra-layout">
        <div className="hidden md:block"><Sidebar /></div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content">
            <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24">
              <div className="w-full space-y-6">{noAccess}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  const teamContent = (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
                  <h1 className="text-2xl font-semibold flex items-center gap-2 text-[var(--dash-text)]">
                    <Users className="h-6 w-6" />
                    Team
                  </h1>
                  <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Invite and manage users for your account.</p>
                </div>
                <Button onClick={() => { setInviteLink(null); setInviteOpen(true); }}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite member
                </Button>
              </div>

              <Card className="border-[var(--dash-border)] bg-[var(--dash-card)] shadow-sm">
                <CardHeader>
                  <CardTitle className="text-[var(--dash-text)]">Team members</CardTitle>
                  <CardDescription className="text-[var(--dash-text-muted)]">People who can log in to this account. You are the owner.</CardDescription>
                </CardHeader>
                <CardContent className="text-[var(--dash-text)]">
                  {isLoading ? (
                    <div className="flex items-center gap-2 text-[var(--dash-text-muted)]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      <li className="flex items-center justify-between py-2 border-b border-[var(--dash-border)]">
                        <div className="flex items-center gap-3">
                          <div className="font-medium">{user.firstName} {user.lastName}</div>
                          <span className="text-sm text-[var(--dash-text-muted)]">{user.email}</span>
                        </div>
                        <Badge variant="secondary">Owner</Badge>
                      </li>
                      {teamUsers.map((u) => (
                        <li key={u.id} className="flex items-center justify-between py-2 border-b border-[var(--dash-border)]">
                          <div>
                            <div className="font-medium">{u.firstName} {u.lastName}</div>
                            <span className="text-sm text-[var(--dash-text-muted)]">{u.email}{u.phone ? ` · ${u.phone}` : ""}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {!u.acceptedAt && (
                              <>
                                <Badge variant="outline">Pending</Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resendInviteMutation.mutate(u.id)}
                                  disabled={resendInviteMutation.isPending}
                                >
                                  {resendInviteMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Send className="h-4 w-4 mr-1" />
                                      Resend invitation
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                            <Badge variant={u.isActive ? "default" : "secondary"}>
                              {u.role.replace("_", " ")}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-bg)]"
                              onClick={() => setEditingMember(u)}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => setRemoveMember(u)}
                              disabled={removeMemberMutation.isPending}
                            >
                              <UserMinus className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
      </div>
      <AlertDialog open={!!removeMember} onOpenChange={(open) => !open && setRemoveMember(null)}>
        {/* dashboard-2025 so portaled dialog (renders in body) gets --dash-* CSS variables */}
        <AlertDialogContent className="dashboard-2025 max-w-md rounded-xl border-2 border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] shadow-xl text-left">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-[var(--dash-text)]">Remove from team</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[var(--dash-text-muted)]">
              {removeMember
                ? `${removeMember.firstName} ${removeMember.lastName} will lose access to this account and will no longer be able to log in. You can invite them again later if needed.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0 border-[var(--dash-border)] bg-[var(--dash-bg)] text-[var(--dash-text)] hover:bg-[var(--dash-border)]" onClick={() => setRemoveMember(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white border-0"
              onClick={() => removeMember && removeMemberMutation.mutate(removeMember.id)}
            >
              {removeMemberMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
        <DialogContent className="sm:max-w-md rounded-xl border-2 border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[var(--dash-text)]">Edit team member</DialogTitle>
            <DialogDescription className="text-sm text-[var(--dash-text-muted)]">
              Update email, name, phone, or role.
            </DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label className="text-[var(--dash-text)]">Email</Label>
                <Input
                  type="email"
                  placeholder="name@company.com"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-[var(--dash-text)]">First name</Label>
                  <Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} className="border-[var(--dash-border)] bg-[var(--dash-bg)]" />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[var(--dash-text)]">Last name</Label>
                  <Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} className="border-[var(--dash-border)] bg-[var(--dash-bg)]" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label className="text-[var(--dash-text)]">Phone (optional)</Label>
                <Input type="tel" placeholder="(555) 123-4567" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="border-[var(--dash-border)] bg-[var(--dash-bg)]" />
              </div>
              <div className="grid gap-2">
                <Label className="text-[var(--dash-text)]">Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="border-[var(--dash-border)] bg-[var(--dash-bg)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingMember(null)} className="border-[var(--dash-border)]">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateMemberMutation.isPending} className="bg-[var(--dash-accent)] hover:opacity-90">
              {updateMemberMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={inviteOpen} onOpenChange={(open) => !open && closeInviteDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{inviteLink ? "Invite link ready" : "Invite team member"}</DialogTitle>
            <DialogDescription>
              {inviteLink
                ? "Share this link so they can set their password and log in."
                : "They’ll get an invite link to set their password."}
            </DialogDescription>
          </DialogHeader>
          {inviteLink ? (
            <div className="flex gap-2">
              <Input readOnly value={inviteLink} className="font-mono text-sm" />
              <Button variant="outline" size="icon" onClick={copyInviteLink}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-xs text-[var(--dash-text-muted)]">Use the full address (e.g. name@company.com) so the invitation delivers.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>First name</Label>
                    <Input
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Last name</Label>
                    <Input
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Phone (optional, for client portal / estimator contact)</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeInviteDialog}>
                  Cancel
                </Button>
                <Button onClick={handleInvite} disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Send invite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedInSettings) return teamContent;
  return (
    <div className="remodra-layout">
      <div className="hidden md:block"><Sidebar /></div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24">
            <div className="w-full space-y-6">
              {teamContent}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
